/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';
import { SxProp } from 'theme-ui';

import theme from 'theme';

import { Typography } from 'components/_common/Typography';

import locationsSelectors from 'locations/locations.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

type LocationCaseSummaryProps = SxProp & {
  locationName: string;
  cases: number;
  isPrimaryAoi?: boolean;
  isGlobalAoi?: boolean;
};

const LocationCaseSummary: React.FC<LocationCaseSummaryProps> = ({
  locationName,
  cases,
  isPrimaryAoi = false,
  isGlobalAoi = false,
  ...props
}) => {
  return (
    <div
      data-testid="location-case-summary"
      sx={{
        width: 'calc(50% - 2px)',
        padding: '12px',
        backgroundColor: theme.colors.deepSea10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      {...props}
    >
      <Typography variant="h6" color={isPrimaryAoi ? 'seafoam100' : 'stone90'} marginBottom="0">
        {cases != null && cases >= 0 ? cases.toLocaleString() : '—'}
      </Typography>
      <Typography
        variant="caption"
        color={isPrimaryAoi ? 'seafoam100' : 'stone70'}
        marginBottom="0"
      >
        {isGlobalAoi ? 'Global cases' : `Cases in ${locationName}`}
      </Typography>
    </div>
  );
};

type RiskOverviewCardGeotreeCaseDetailsProps = {
  locationId: number;
  diseaseId: number;
};

const RiskOverviewCardGeotreeCaseDetails: React.FC<RiskOverviewCardGeotreeCaseDetailsProps> = ({
  locationId,
  diseaseId,
}) => {
  const locationName = useSelector(locationsSelectors.getLocationName(locationId));

  const geotreeCases = useSelector(riskOverviewSelectors.getNestedCasesFlat(diseaseId, locationId));

  return (
    <div
      data-testid="card-geotree-summary"
      className="ro-geotree-cases"
      sx={{ marginLeft: [null, '24px'], width: '100%' }}
    >
      {!geotreeCases?.length ? (
        <div
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          <LocationCaseSummary
            locationName={locationName}
            cases={null}
            isPrimaryAoi
            sx={{ marginRight: '2px' }}
          />
          <LocationCaseSummary
            locationName="Global"
            cases={null}
            isGlobalAoi
            sx={{ marginLeft: '2px' }}
          />
        </div>
      ) : (
        <>
          <div
            sx={{
              display: 'flex',
              width: '100%',
              height: geotreeCases.length > 2 ? null : '100%',
              marginBottom: geotreeCases.length > 2 ? '2px' : null,
            }}
          >
            <LocationCaseSummary
              locationName={locationName}
              cases={geotreeCases[0].cases}
              isPrimaryAoi
              isGlobalAoi={geotreeCases[0].isGlobal}
              sx={{ marginRight: '2px' }}
            />
            <LocationCaseSummary
              locationName={geotreeCases[1]?.locationName}
              cases={geotreeCases[1]?.cases}
              isGlobalAoi={geotreeCases[1]?.isGlobal}
              sx={{ marginLeft: '2px' }}
            />
          </div>
          {geotreeCases.length > 2 && (
            <div sx={{ display: 'flex', width: '100%', marginTop: '2px' }}>
              <LocationCaseSummary
                locationName={geotreeCases[2].locationName}
                cases={geotreeCases[2].cases}
                isGlobalAoi={geotreeCases[2].isGlobal}
                sx={{ marginRight: '2px' }}
              />
              {geotreeCases[3] && (
                <LocationCaseSummary
                  locationName={geotreeCases[3].locationName}
                  cases={geotreeCases[3].cases}
                  isGlobalAoi={geotreeCases[3].isGlobal}
                  sx={{ marginLeft: '2px' }}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RiskOverviewCardGeotreeCaseDetails;
